var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Box, Typography } from '@mui/material';
import React from 'react';
import { ariaSubTitleId, ariaTitleAdditionalInfoId, ariaTitleId } from './ariaIds';
/**
 * Title area component. Should be used as the first child component of `ModalSlideoutHeader`.
 * The component itself also accepts children. Area for that component is located next to the title. Examples would be
 * `Chip`, or some `input` components to render according to the style you provide in `sx` prop.
 * Bellow title and children is sub-title.
 *
 * @param {object} props - Component props.
 * @param {"h1" | "h2" | "h3" | "h4" | "h5" | "h6" | undefined} [props.titleTypographyVariant] - Optional variant for the title typography.
 * @param {React.ReactNode} [props.subTitle] - Optional subtitle displayed below the title.
 * @param {React.ReactNode} props.title - The main title content.
 * @param {SxProps<Theme>} [props.sx] - Optional MUI style overrides for the container.
 * @param {boolean} [props.ariaDescribeAllChildren=true] - If true, includes children in ARIA description.
 * @param {React.ReactNode} [props.children] - Additional elements rendered next to the title.
 * @returns {React.ReactElement} Rendered title area element.
 */
export const ModalSlideoutTitleArea = (_a) => {
    var { titleTypographyVariant, subTitle, title, sx, ariaDescribeAllChildren = true, children } = _a, props = __rest(_a, ["titleTypographyVariant", "subTitle", "title", "sx", "ariaDescribeAllChildren", "children"]);
    return (React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, "data-test": props['data-test'] },
        React.createElement(Box, { sx: [{ display: 'flex' }, ...(Array.isArray(sx) ? sx : [sx])] },
            React.createElement(Typography, { id: ariaTitleId, variant: titleTypographyVariant, sx: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                    WebkitLineClamp: 4,
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                } }, title),
            React.createElement(Box, { id: ariaDescribeAllChildren ? ariaTitleAdditionalInfoId : undefined }, children)),
        subTitle && (React.createElement(Box, { sx: { alignItems: 'center', display: 'flex' } },
            React.createElement(Typography, { id: ariaSubTitleId, variant: 'body2' }, subTitle)))));
};
